/**
 * Client-side Analytics Initialization
 * Handles GTM, Google Analytics, Hotjar, and Meta Pixel initialization
 * This replaces the legacy public/index.js script
 */

declare global {
  interface Window {
    dataLayer: any[];
    fbq?: (...args: any[]) => void;
    _fbq?: (...args: any[]) => void;
    gtag?: (...args: any[]) => void;
    hj?: (...args: any[]) => void;
    _hjSettings?: any;
    __onbMetaPixelInitialized?: boolean;
    __onbMetaPixelId?: string;
  }
}

/**
 * Initialize Google Tag Manager and Google Analytics
 */
export function initAnalytics(gaId?: string): void {
  if (!gaId) {
    console.warn("[Analytics] GA_ID not provided, skipping initialization");
    return;
  }

  // Only enable on allowed hosts
  const isEnabled = [
    "localhost",
    "misolicitud.santander.com.uy",
    "onboarding-pre.uy.corp",
  ].some(
    (host) =>
      window.location.href.indexOf(host) > -1 &&
      typeof gaId === "string" &&
      gaId.indexOf("%") === -1,
  );

  if (!isEnabled) {
    console.log("[Analytics] Not enabled for current host");
    return;
  }

  window.dataLayer = window.dataLayer || [];

  /* Google Tag Manager */
  (function (w: any, d: any, s: any, l: any, i: any) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l !== "dataLayer" ? "&l=" + l : "";
    j.async = false;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", "GTM-TWG345R");
  /* End Google Tag Manager */

  /* Global site tag (gtag.js) - Google Analytics */
  (function () {
    const gaScript = document.createElement("script");
    gaScript.async = false;
    gaScript.src = "https://www.googletagmanager.com/gtag/js?id=" + gaId;
    document.head.appendChild(gaScript);
    gaScript.onload = function () {
      window.dataLayer.push({ event: "gtm.js" });
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      if (window.gtag) {
        window.gtag("js", new Date());
        window.gtag("config", gaId);
      }
    };
  })();
  /* End Google Analytics */

  console.log("[Analytics] GTM and GA initialized with ID:", gaId);
}

/**
 * Initialize Hotjar tracking
 */
export function initHotjar(): void {
  if (window.location.protocol !== "https:") {
    return;
  }

  (function (h: any, o: any, t: any, j: any) {
    h.hj =
      h.hj ||
      function () {
        (h.hj.q = h.hj.q || []).push(arguments);
      };
    h._hjSettings = { hjid: 3066922, hjsv: 6 };
    const a = o.getElementsByTagName("head")[0];
    const r = o.createElement("script");
    r.async = 1;
    r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
    a.appendChild(r);
  })(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=");

  console.log("[Analytics] Hotjar initialized");
}

/**
 * Initialize Meta Pixel (Facebook Pixel)
 */
export function initMetaPixel(pixelId?: string): void {
  if (!pixelId) {
    console.warn(
      "[Analytics] Pixel ID not provided, skipping Meta Pixel initialization",
    );
    return;
  }

  if (window.__onbMetaPixelInitialized && window.__onbMetaPixelId === pixelId) {
    return;
  }

  (function (f: any, b: any, e: any, v: any) {
    let n: any, t: any, s: any;
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js",
  );

  if (window.fbq) {
    window.fbq("init", pixelId);
    window.fbq("track", "PageView");
  }

  const noscriptTag = document.createElement("noscript");
  const noscriptImg = document.createElement("img");
  noscriptImg.height = 1;
  noscriptImg.width = 1;
  noscriptImg.style.display = "none";
  noscriptImg.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscriptTag.appendChild(noscriptImg);
  document.head.appendChild(noscriptTag);

  window.__onbMetaPixelInitialized = true;
  window.__onbMetaPixelId = pixelId;

  console.log("[Analytics] Meta Pixel initialized with ID:", pixelId);
}

/**
 * Initialize all client-side analytics services
 */
export function initializeAllAnalytics(): void {
  const env = (
    import.meta as unknown as { env: Record<string, string | undefined> }
  ).env;

  const gaId = env.VITE_ONBOARDING_ANALYTICS_ID;
  const pixelId = env.VITE_META_PIXEL_ID;

  // Always initialize GTM and Hotjar
  initAnalytics(gaId);
  initHotjar();

  // Initialize Meta Pixel if pixel ID is available
  if (pixelId) {
    initMetaPixel(pixelId);
  }
}
